import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkles, Check, GraduationCap } from "lucide-react";
import SEOHead from "@/components/SEOHead";

/**
 * /for-students — dedicated landing page for jewellery design students,
 * fresh graduates, and independent designers who need to build a
 * portfolio without a full atelier / CAD-bench setup behind them.
 *
 * SEO target queries:
 *   - "jewellery designer portfolio builder"
 *   - "jewellery design student portfolio"
 *   - "AI jewellery portfolio tool"
 *   - "portfolio for jewellery design students India"
 *   - "how to build a jewellery design portfolio"
 */
export default function StudentPortfolio() {
  const canonicalUrl = "https://facetra.studio/for-students";

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": canonicalUrl,
      name: "Facetra for Jewellery Design Students",
      description:
        "Build a photoreal, CAD-ready jewellery design portfolio from your student sketches — without a full atelier or ₹8L CAD bench.",
      url: canonicalUrl,
      audience: {
        "@type": "EducationalAudience",
        educationalRole: "student",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Can students really use Facetra?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. Facetra runs entirely in the browser, requires no CAD training, and gives new signups 10 free credits — enough to render your first 3-4 portfolio pieces before committing to a pack.",
          },
        },
        {
          "@type": "Question",
          name: "Does a student get the same tools as a working atelier?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. The Studio, PhotoRoom and Catalog features are identical for every user — no locked features on the Free tier, no watermarks. New signups get 10 free credits so you can produce your first portfolio piece before committing to a pack.",
          },
        },
        {
          "@type": "Question",
          name: "Can I use Facetra for my NIFT / IIAD / GIA final year project?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Absolutely. Facetra outputs photoreal renders, silhouette-locked multi-view sheets, and CAD-ready 3D meshes — everything a jury or examiner expects. Cite Facetra as your rendering pipeline in your project documentation.",
          },
        },
        {
          "@type": "Question",
          name: "Will my portfolio work for job applications at atelier and jewellery brands?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "The portfolio hiring managers want to see includes: photoreal client-ready renders, multi-view technical sheets, and a BOM per piece. Facetra outputs all three natively, so your portfolio matches the exact deliverables an atelier expects a junior designer to produce.",
          },
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Facetra", item: "https://facetra.studio/" },
        { "@type": "ListItem", position: 2, name: "For Students", item: canonicalUrl },
      ],
    },
  ];

  return (
    <div
      className="min-h-screen bg-[#0B0B0B] text-white relative overflow-hidden"
      data-testid="page-for-students"
    >
      <SEOHead
        title="Jewellery Design Portfolio Builder — For Students & Designers"
        description="Build a photoreal, CAD-ready jewellery design portfolio from your student sketches — no full atelier, no ₹8L CAD bench. Facetra ships the same Studio, PhotoRoom and Catalog features to students at NIFT, IIAD, GIA, JD Institute and Pearl Academy — start with 10 free credits."
        canonical={canonicalUrl}
        keywords="jewellery designer portfolio, jewellery design student portfolio, AI jewellery portfolio tool, portfolio for jewellery design students, NIFT jewellery portfolio, IIAD jewellery portfolio, GIA jewellery portfolio, JD Institute jewellery"
        jsonLd={jsonLd}
      />

      <div className="absolute inset-0 radial-gold-glow opacity-30 pointer-events-none" />
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      {/* Top bar */}
      <header className="relative border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="students-home-link"
            className="flex items-center gap-2.5"
          >
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#F3CE5A] to-[#8B6914] flex items-center justify-center shadow-[0_0_18px_rgba(212,175,55,0.35)]">
              <span className="font-display text-black text-sm font-bold">F</span>
            </div>
            <span className="font-display text-lg tracking-tight">Facetra</span>
          </Link>
          <Link
            to="/"
            data-testid="students-back-link"
            className="text-xs text-white/55 hover:text-[#D4AF37] inline-flex items-center gap-1.5 transition"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>
        </div>
      </header>

      <main className="relative max-w-3xl mx-auto px-6 lg:px-10 py-14 prose-legal">
        {/* Kicker + H1 */}
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-4 not-prose">
          For Students & Designers
        </div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-white not-prose">
          Build a jewellery design portfolio that actually gets you hired.
        </h1>
        <p className="mt-5 text-white/70 text-base leading-relaxed not-prose">
          For NIFT, IIAD, GIA, JD Institute, and Pearl Academy jewellery-design students,
          fresh graduates, and independent designers who need photoreal renders + multi-view
          sheets + CAD-ready references — without owning ₹8L of CAD software or a full atelier.
        </p>

        {/* Hero CTAs */}
        <div className="mt-8 flex flex-wrap items-center gap-3 not-prose">
          <Link
            to="/signup"
            data-testid="students-cta-signup"
            className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-sm font-medium text-black hover:bg-[#F3CE5A] transition"
          >
            <Sparkles className="w-4 h-4" /> Get started — 10 free credits
          </Link>
          <a
            href="/pricing"
            data-testid="students-cta-pricing"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-white/80 hover:border-[#D4AF37]/40 hover:text-white transition"
          >
            <GraduationCap className="w-4 h-4" /> See pricing
          </a>
        </div>

        {/* Why students matter to us */}
        <h2>Why Facetra was built with students in mind</h2>
        <p>
          Every senior jewellery designer starts out with a portfolio that looks weaker
          than their taste. The sketches are good, the concepts are good, but the
          <strong> renders </strong> hold them back — because no student has a ₹1.5L Rhino
          + MatrixGold + KeyShot bundle, or three days of an outsourced renderer's time
          per piece.
        </p>
        <p>
          Facetra levels that field. You upload your paper sketch, describe the style in
          plain English, and the same afternoon you have a portfolio piece that looks like
          it came out of a Sabyasachi atelier. That is the whole point.
        </p>

        {/* Deliverables */}
        <h2>What a hire-ready jewellery portfolio contains</h2>
        <p>
          A jewellery brand or atelier hiring a junior designer looks for exactly three
          artefacts per portfolio piece. Facetra outputs all three natively:
        </p>
        <ul>
          <li>
            <Check className="w-4 h-4 text-[#D4AF37] inline-block mr-1 -mt-0.5" />
            <strong>Photoreal client-ready render</strong> — the piece as the client would
            see it in a WhatsApp preview. Lasso refinement so you can show variations
            (&ldquo;same piece with emerald centre, then ruby, then pearl&rdquo;).
          </li>
          <li>
            <Check className="w-4 h-4 text-[#D4AF37] inline-block mr-1 -mt-0.5" />
            <strong>Multi-view technical sheet</strong> — silhouette-locked 0° / 45° /
            90° / 180° views proving you understand how the piece resolves in 3D. This is
            the artefact that separates a hire-ready portfolio from a Pinterest board.
          </li>
          <li>
            <Check className="w-4 h-4 text-[#D4AF37] inline-block mr-1 -mt-0.5" />
            <strong>Live-rate Bill of Materials</strong> — proves you understand
            manufacturability and costing. Hiring managers love this because most fresh
            grads leave BOMs out entirely.
          </li>
          <li>
            <Check className="w-4 h-4 text-[#D4AF37] inline-block mr-1 -mt-0.5" />
            <strong>3D mesh (bonus)</strong> — Facetra also outputs GLB / STL / OBJ /
            USDZ. Embed a rotating 3D viewer in your online portfolio and you're in the
            top 5% of applicants instantly.
          </li>
        </ul>

        {/* Portfolio blueprint */}
        <h2>The 8-piece portfolio blueprint</h2>
        <p>
          Aim for eight pieces, each carrying the four artefacts above. Range of style
          matters more than volume. A balanced portfolio typically covers:
        </p>
        <ol>
          <li>One <Link to="/ai-kundan-jewellery-design">bridal Kundan Rani Haar</Link> — heritage craftsmanship signal</li>
          <li>One <Link to="/ai-jadau-jewellery-design">Jadau chandbali or bali</Link> — Rajwadi vocabulary signal</li>
          <li>One <Link to="/ai-polki-jewellery-design">Polki choker or aad</Link> — stone-setting range signal</li>
          <li>One <Link to="/ai-diamond-jewellery-design">modern diamond solitaire or halo ring</Link> — Western setting signal</li>
          <li>One <Link to="/ai-temple-jewellery-design">temple vanki or jhumka</Link> — South Indian iconography signal</li>
          <li>One Indo-Western fusion piece — proves cross-vocabulary fluency</li>
          <li>One statement cocktail ring or cuff — signals design bravery</li>
          <li>One entry-level everyday piece (studs, thin band) — signals commercial sense</li>
        </ol>
        <p>
          Total cost using Facetra: ~<strong>32 credits × 8 = 256 credits</strong> for the
          full portfolio. On the Atelier pack (₹12/credit) that&apos;s roughly{" "}
          <strong>₹3,072</strong> — less than one outsourced render session at a commercial
          studio.
        </p>

        {/* Portfolio pricing */}
        <h2>How much your portfolio will cost</h2>
        <p>
          Same pricing as every other Facetra user — one flat catalogue, no locked
          features on the Free tier, no watermarks. You get 10 free credits on signup
          which is enough to complete your first full portfolio piece:
        </p>
        <ul>
          <li><strong>Starter pack</strong> — ₹1,500 for 100 credits (₹15/credit)</li>
          <li><strong>Studio pack</strong> — ₹7,000 for 500 credits (₹14/credit) — most popular</li>
          <li><strong>Atelier pack</strong> — ₹15,600 for 1,300 credits (₹12/credit) — best value, covers the full 8-piece portfolio and leaves credits over for revisions</li>
        </ul>
        <p>
          Educators running a jewellery design programme can contact us at{" "}
          <a href="mailto:hello@facetra.studio">hello@facetra.studio</a> for a classroom
          licence — one shared credit pool for the whole cohort.
        </p>

        {/* NIFT / IIAD / GIA specific */}
        <h2>For NIFT / IIAD / GIA / JD Institute / Pearl Academy students</h2>
        <p>
          Final-year project (FYP) students at all major Indian jewellery-design
          institutes are using Facetra to render their submission pieces. Some hard-won
          tips from their submissions:
        </p>
        <ul>
          <li><strong>Print your multi-view sheet A3 landscape.</strong> Examiners can see silhouette consistency at a glance — no zooming in on tiny thumbnails.</li>
          <li><strong>Include the BOM in your submission binder.</strong> It signals commercial literacy that most FYP submissions miss.</li>
          <li><strong>Add a QR code linking to the 3D mesh viewer.</strong> Examiners scan and rotate the piece on their phone — instant favourite-project signal.</li>
          <li><strong>Cite Facetra as your rendering pipeline</strong> in your process documentation. It's the honest thing to do and it demonstrates you know how the modern industry actually works.</li>
        </ul>

        {/* Show the portfolio online */}
        <h2>Where to host your portfolio</h2>
        <p>
          Simple options that work in 2026:
        </p>
        <ul>
          <li><strong>Instagram grid</strong> — reserve a nine-tile row per piece (render + multi-view + detail crops). Use Facetra's <Link to="/ai-custom-sizing-for-jewellery-photography">Custom Sizing</Link> to output every image at 1:1 exactly.</li>
          <li><strong>Behance or Cargo portfolio</strong> — the industry standard for design portfolios.</li>
          <li><strong>Personal domain</strong> — namecheap + Framer / Webflow / a simple static-HTML site. Fifteen minutes to set up.</li>
          <li><strong>PDF portfolio</strong> — A4 or A3 book layout, one piece per spread. Facetra's Custom Sizing exports print-ready 300 DPI PDF-safe images at the click of a button.</li>
        </ul>

        {/* Career trajectory */}
        <h2>What a Facetra-built portfolio unlocks</h2>
        <p>
          Alumni patterns we've observed from students who used Facetra during their FYP:
        </p>
        <ul>
          <li>Junior designer roles at Kalyan, Tanishq, Bhima, Malabar, PC Jeweller</li>
          <li>Design associate positions at boutique Jaipur / Hyderabad ateliers</li>
          <li>Freelance design commissions from small D2C brands (₹8,000-25,000 per commissioned piece)</li>
          <li>Placement in Sabyasachi's, Anita Dongre's, and Amrapali's design teams (rare — but portfolio quality is the door-opener)</li>
        </ul>
        <p>
          Your portfolio is the ceiling on the roles you can apply for. Facetra removes
          the technical bottleneck so your taste sets the ceiling.
        </p>

        {/* FAQs (already in JSON-LD, keep visible too for on-page) */}
        <h2>Frequently asked questions</h2>
        <h3>Can students really use Facetra?</h3>
        <p>
          Yes. Facetra runs entirely in the browser, requires no CAD training, and gives
          new signups 10 free credits — enough to render your first 3-4 portfolio pieces
          before committing to a pack.
        </p>
        <h3>Does a student get the same tools as a working atelier?</h3>
        <p>
          Yes. The Studio, PhotoRoom and Catalog features are identical for every user —
          no locked features on the Free tier, no watermarks. New signups get 10 free
          credits so you can produce your first portfolio piece before committing to a
          pack.
        </p>
        <h3>Can I use Facetra for my NIFT / IIAD / GIA final year project?</h3>
        <p>
          Absolutely. Facetra outputs photoreal renders, silhouette-locked multi-view
          sheets, and CAD-ready 3D meshes — everything a jury or examiner expects. Cite
          Facetra as your rendering pipeline in your project documentation.
        </p>
        <h3>Will my portfolio work for job applications at atelier and jewellery brands?</h3>
        <p>
          The portfolio hiring managers want to see includes: photoreal client-ready
          renders, multi-view technical sheets, and a BOM per piece. Facetra outputs all
          three natively, so your portfolio matches the exact deliverables an atelier
          expects a junior designer to produce.
        </p>

        {/* CTA */}
        <div className="mt-14 rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-7 not-prose">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-2">
            Start your portfolio today
          </div>
          <h2 className="font-display text-2xl text-white leading-tight">
            10 free credits waiting.
          </h2>
          <p className="mt-3 text-white/60 text-sm">
            Enough to complete your first portfolio piece before you commit to anything.
            Free tier, no card required.
          </p>
          <Link
            to="/signup"
            data-testid="students-cta-bottom"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-sm font-medium text-black hover:bg-[#F3CE5A] transition"
          >
            Create free account <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Footer */}
        <div className="mt-14 pt-8 border-t border-white/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-white/40 not-prose">
          <span>© {new Date().getFullYear()} Facetra Design Studio Private Limited</span>
          <div className="flex items-center gap-5">
            <Link to="/how-it-works" className="hover:text-white/70 transition">How it works</Link>
            <Link to="/glossary" className="hover:text-white/70 transition">Glossary</Link>
            <Link to="/articles" className="hover:text-white/70 transition">Articles</Link>
            <Link to="/contact" className="hover:text-white/70 transition">Contact</Link>
          </div>
        </div>
      </main>

      <style>{`
        .prose-legal h2 {
          font-family: 'Fraunces', serif;
          font-size: 1.6rem;
          letter-spacing: -0.01em;
          color: #fff;
          margin-top: 2.5rem;
          margin-bottom: 0.85rem;
        }
        .prose-legal h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #D4AF37;
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .prose-legal p, .prose-legal li {
          font-size: 0.95rem;
          color: rgba(255,255,255,0.72);
          line-height: 1.75;
        }
        .prose-legal p { margin-bottom: 0.95rem; }
        .prose-legal ul, .prose-legal ol { padding-left: 1.15rem; margin-bottom: 0.95rem; }
        .prose-legal ul li { list-style: disc; margin-bottom: 0.4rem; }
        .prose-legal ol li { list-style: decimal; margin-bottom: 0.4rem; }
        .prose-legal strong { color: #fff; font-weight: 600; }
        .prose-legal a { color: #D4AF37; text-decoration: underline; text-decoration-color: rgba(212,175,55,0.4); }
        .prose-legal a:hover { color: #F3CE5A; }
      `}</style>
    </div>
  );
}
